import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../auth/store';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  reviewsApi,
  testimonialsApi,
  type CurationStatus,
  type ReviewListParams,
  type TestimonialListParams,
} from '../api/reviewsApi';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const REVIEWS_KEY = 'reviews';
const TESTIMONIALS_KEY = 'testimonials';

export const useReviews = (params: ReviewListParams) => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  return useQuery({
    queryKey: [REVIEWS_KEY, tenantId, params],
    queryFn: () => reviewsApi.list(params),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
};

export const useTestimonials = (params: TestimonialListParams) => {
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  return useQuery({
    queryKey: [TESTIMONIALS_KEY, tenantId, params],
    queryFn: () => testimonialsApi.list(params),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
};

/**
 * Mutation hooks below always invalidate the WHOLE `['reviews'|'testimonials', tenantId]`
 * prefix rather than patching the cache in place — a status change can silently clear
 * `isFeatured` server-side (moving out of APPROVED auto-unfeatures), so a targeted cache
 * patch could leave a stale `isFeatured: true` on screen. A refetch is the only way to
 * stay correct.
 */
export const useSetReviewStatus = () => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: CurationStatus }) => reviewsApi.setStatus(id, status),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY, tenantId] });
      toast.success(status === 'APPROVED' ? 'Review approved' : status === 'REJECTED' ? 'Review rejected' : 'Review status updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update review status')),
  });
};

export const useSetReviewFeatured = () => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, isFeatured }: { id: string; isFeatured: boolean }) => reviewsApi.setFeatured(id, isFeatured),
    onSuccess: (_, { isFeatured }) => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY, tenantId] });
      toast.success(isFeatured ? 'Featured on storefront' : 'Removed from featured');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update featured status')),
  });
};

export const useDeleteReview = () => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => reviewsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY, tenantId] });
      toast.success('Review deleted');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to delete review')),
  });
};

export const useSetTestimonialStatus = () => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: CurationStatus }) => testimonialsApi.setStatus(id, status),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: [TESTIMONIALS_KEY, tenantId] });
      toast.success(
        status === 'APPROVED' ? 'Testimonial approved' : status === 'REJECTED' ? 'Testimonial rejected' : 'Testimonial status updated'
      );
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update testimonial status')),
  });
};

export const useSetTestimonialFeatured = () => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, isFeatured }: { id: string; isFeatured: boolean }) => testimonialsApi.setFeatured(id, isFeatured),
    onSuccess: (_, { isFeatured }) => {
      queryClient.invalidateQueries({ queryKey: [TESTIMONIALS_KEY, tenantId] });
      toast.success(isFeatured ? 'Featured on storefront' : 'Removed from featured');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update featured status')),
  });
};

export const useDeleteTestimonial = () => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.selectedTenantId);
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => testimonialsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TESTIMONIALS_KEY, tenantId] });
      toast.success('Testimonial deleted');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to delete testimonial')),
  });
};
